import type { BoxProps } from '@hover/blueprint';
import { Box, Button, Center, Heading, Icon, Image } from '@hover/blueprint';
import { iChevronLeft } from '@hover/icons';
import { AnimatePresence } from 'framer-motion';
import type { RefObject } from 'react';
import React, { useMemo } from 'react';
import { generatePath, useHistory, useParams } from 'react-router';

import type { AiDisplayImage, Maybe } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { Route } from 'src/shared/route';

import { FullscreenImageThumbnail } from '../../FullscreenImage';
import { InfiniteCarousel } from '../InfiniteCarousel';

const CAROUSEL_CONTROLS_BACKGROUND = 'darkAlpha.700';

interface ProjectCarouselProps extends BoxProps {
  addables: Maybe<AiDisplayImage[]> | undefined;
  projectNameVisibilityHandler?: RefObject<HTMLDivElement>;
}

const getProjectImages = (assets: Maybe<AiDisplayImage[]> | undefined) => {
  const images: {
    generatedImage: AiDisplayImage;
    src: string;
    edited: boolean;
  }[] = [];

  assets?.forEach(asset => {
    images.push({
      generatedImage: asset,
      src:
        asset.aiGeneratedImage.generatedImagePath ||
        asset.aiGeneratedImage.imagePath,
      edited: !!asset.aiGeneratedImage.edits,
    });
  });

  return images;
};

const ProjectCarousel = ({
  projectNameVisibilityHandler,
  addables,
  ...rest
}: ProjectCarouselProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

  const images = useMemo(
    () => getProjectImages(addables).reverse() || [],
    [addables],
  );
  const history = useHistory();
  const { projectId } = useParams<{ projectId: string }>();

  return (
    <Box
      height={{ base: '460px', tablet: '100dvh' }}
      overflow="hidden"
      position={{ base: 'relative', tablet: 'sticky' }}
      top={0}
      {...rest}
    >
      <Box
        alignItems="center"
        display={{ base: 'none', tablet: 'flex' }}
        p={800}
        position="absolute"
        top={0}
        width="100%"
        zIndex={1}
      >
        <Button
          colorScheme="light"
          height={350}
          label="back"
          onClick={() => {
            history.push(Route.HOMEOWNER_APP_PROJECTS_LIST.path);
          }}
          shape="circle"
          variant="primary"
          width={350}
        >
          <Icon color="neutral.900" icon={iChevronLeft} />
        </Button>
      </Box>
      <Box
        alignItems="center"
        display={{ base: 'none', tablet: 'flex' }}
        p={800}
        position="absolute"
        top={0}
        width="100%"
        zIndex={1}
      >
        <Button
          colorScheme="light"
          height={350}
          label="back"
          onClick={() => {
            history.push(Route.HOMEOWNER_APP_PROJECTS_LIST.path);
          }}
          shape="circle"
          variant="primary"
          width={350}
        >
          <Icon color="neutral.900" icon={iChevronLeft} />
        </Button>
      </Box>
      <AnimatePresence initial={false}>
        <InfiniteCarousel
          controlsBackground={CAROUSEL_CONTROLS_BACKGROUND}
          numberOfElements={images.length}
        >
          {({ index }) => {
            const modulo = index % images.length;
            const imageIndex = modulo < 0 ? images.length + modulo : modulo;
            const { generatedImage, src, edited } = images?.[imageIndex] || {};

            if (!src)
              return (
                <Box
                  backgroundColor="neutral.100"
                  cursor="auto"
                  height="100%"
                  width="100%"
                />
              );

            return (
              <FullscreenImageThumbnail image={generatedImage} width="100%">
                <Image
                  alt={t('projects.projectCarouselImageAlt')}
                  draggable={false}
                  height="100%"
                  objectFit="cover"
                  onClick={() =>
                    history.push(
                      generatePath(
                        Route.HOMEOWNER_APP_PROJECT_DISPLAY_FULLSCREEN.path,
                        {
                          projectId,
                          aiDisplayImageId: generatedImage.id,
                        },
                      ),
                    )
                  }
                  src={src}
                  width="100%"
                />
                {/* Image footer */}
                <Box
                  alignItems="center"
                  bottom={0}
                  pb={{ base: 500, tablet: 800 }}
                  position="absolute"
                  px={{ base: 500, tablet: 800 }}
                  ref={projectNameVisibilityHandler}
                  width="100%"
                >
                  {edited && (
                    <Center
                      backgroundColor={CAROUSEL_CONTROLS_BACKGROUND}
                      borderRadius={200}
                      height={200}
                      px={100}
                    >
                      <Heading color="neutral.100" m={0} size={200}>
                        {t('projects.edited')}
                      </Heading>
                    </Center>
                  )}
                </Box>
              </FullscreenImageThumbnail>
            );
          }}
        </InfiniteCarousel>
      </AnimatePresence>
    </Box>
  );
};

export { ProjectCarousel };
